import type { ThemeProps } from "models/styles/theme";
import styled from "styled-components/macro";
import { errorInputCss } from "styles/helpers/errors";
import { CssFlex, CssGrid, CssIcon } from "styles/helpers/layout";

export const StyledSelectContainer = styled.div`
  position: relative;

  select {
    display: none;
  }
`;

interface SelectProps extends ThemeProps {
  $hasError?: boolean;
  $isDisabled?: boolean;
}

export const StyledSelect = styled(CssGrid)<SelectProps>`
  cursor: pointer;
  border-radius: 2px;
  border: 1px solid ${(props: ThemeProps) => props.theme.palette.neutral._600};
  background-color: ${(props: ThemeProps) => props.theme.palette.neutral.white};

  width: 100%;

  padding: 0.75rem 1rem;
  font-size: ${(props: ThemeProps) => props.theme.font.size._16};
  line-height: 1.5rem;

  span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    min-height: 1.5rem;
  }

  ${(props: SelectProps) =>
    props.$isDisabled &&
    `
    color: ${props.theme.palette.neutral._600};
    cursor: not-allowed;
  `}

  ${({ $hasError }) => $hasError && errorInputCss}
`;

interface OptionListProps extends ThemeProps {
  $isOpen?: boolean;
}

export const StyledOptionList = styled.ul<OptionListProps>`
  position: absolute;
  top: 3.2rem;
  width: 100%;
  padding: 0;
  margin: 0;
  min-width: fit-content;

  z-index: 999;
  list-style: none;
  background: ${(props: ThemeProps) => props.theme.palette.neutral.white};
  border: solid 1px ${(props: ThemeProps) => props.theme.palette.neutral._900};
  border-radius: 2px;

  ${(props: OptionListProps) =>
    !props.$isOpen &&
    `
    display: none;
  `}
`;

interface OptionListItemProps extends ThemeProps {
  $isPlaceholder?: boolean;
  $isSelected?: boolean;
}

export const StyledOptionListItem = styled.li<OptionListItemProps>`
  padding: 0.5rem 1rem;
  font-size: ${(props: ThemeProps) => props.theme.font.size._14};

  ${(props: OptionListItemProps) =>
    props.$isPlaceholder &&
    `
    padding: 1rem;
    color: ${props.theme.palette.neutral._600};
  `}

  ${(props: OptionListItemProps) =>
    props.$isSelected &&
    `
    font-weight: bold;
    color: ${props.theme.palette.secondary.brand};
    background: ${props.theme.palette.secondary._950};
  `}

  ${(props: OptionListItemProps) =>
    !props.$isPlaceholder &&
    `
    &:hover {
      background: ${props.theme.palette.neutral._950};
    }
  `}
`;

export const StyledDisabledOption = styled.li`
  padding: 8px 12px;
  cursor: 'not-allowed';
  color: ${(props: ThemeProps) => props.theme.palette.neutral._400};
  background-color: ${(props: ThemeProps) => props.theme.palette.neutral._950};
  opacity: 0.7;
  pointer-events: none;
`;

export const StyledErrorMessage = styled.span`
  display: block;
  text-align: left;
  margin-top: 0.5rem;
  font-size: ${(props: ThemeProps) => props.theme.font.size._14};
  color: ${(props: ThemeProps) => props.theme.palette.feedback.error.brand};
`;

export const StyledSearchBox = styled(CssFlex)<ThemeProps>`
  margin: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${(props: ThemeProps) => props.theme.palette.neutral._950};
  border-radius: 0.25rem;

  input {
    border: 0;
    outline: none;
    background: none;
    width: 100%;
    font-size: ${(props: ThemeProps) => props.theme.font.size._14};
    color: ${(props: ThemeProps) => props.theme.palette.neutral._500};

    ::placeholder {
      color: ${(props: ThemeProps) => props.theme.palette.neutral._600};
    }
  }

  ${CssIcon} {
    color: ${(props: ThemeProps) => props.theme.palette.neutral._500};
  }
`;
